import type Phaser from "phaser";
import type { WorldManifest } from "daedalus-core";
import { createDomSurface, scenePageAtlas, type ChunkTexturePool, type TileAnimator } from "daedalus-renderer-phaser";
import { GameMapPictures } from "../map-picture/pictures.ts";
import { loadClientChunk, type LoadedWorld } from "../../editor/world-loader.ts";
import type { ChunkLoading } from "../chunk-loading.ts";
import type { WorldBoot } from "../world-boot.ts";
import { gameReport } from "../report.ts";
import { NeighbourGround } from "./ground.ts";
import { cameraBounds, neighbourPlaces, sortReachOf, type NeighbourPlace } from "./reach.ts";

/**
 * What a stage mounts the ground beside its map with: the scene, the boot whose atlas holder loads and lets go the pages,
 * the world and the map the page stands on, the map's Players band, the tile animator, the chunk loading knobs, the chunk
 * textures the scene shares between its painters and the clock, and whether the page is ready to spend frame time on the
 * minimap's pictures.
 */
export interface BesideMount {
  readonly scene: Phaser.Scene;
  readonly boot: WorldBoot;
  readonly world: LoadedWorld;
  readonly map: WorldManifest["maps"][number];
  readonly band: number;
  readonly animator: TileAnimator;
  readonly loading: ChunkLoading;
  readonly textures: ChunkTexturePool;
  readonly now: () => number;
  readonly active: () => boolean;
}

/**
 * The neighbours of a stage's map, or none when the manifest places one off the chunk grid, which a valid world never
 * does; that is told, and the map is drawn alone as it always was.
 *
 * @param world The loaded world.
 * @param map The map the page stands on.
 * @returns The placed neighbours.
 */
function placesOf(world: LoadedWorld, map: WorldManifest["maps"][number]): NeighbourPlace[] {
  try {
    return neighbourPlaces(world.manifest.maps, map.id, world.manifest.chunkSize);
  } catch (error) {
    console.warn(`the ground beside ${map.id} is not drawn: ${error instanceof Error ? error.message : String(error)}`);
    return [];
  }
}

/**
 * Mounts the ground beside a stage's map (ADR-147 decisions 6 to 10): bounds the world camera by the map and its placed
 * neighbours (`cameraBounds`, so it stops as it always did at an edge with none), and makes the neighbour ground that
 * fetches, paints and lets go of their chunks and pages, which the page's report reads until the ground is cleared. A map with no placed neighbour, a room among them, gets the
 * camera bounds it always had and ground that does nothing.
 *
 * @param mount What the stage mounts it with.
 * @returns The ground.
 */
export function mountBeside(mount: BesideMount): NeighbourGround {
  const { world, map, boot } = mount;
  const tileSize = world.manifest.tileSize;
  const places = placesOf(world, map);
  const bounds = cameraBounds(map.width, map.height, places, { left: 0, top: 0, right: 0, bottom: 0 });
  mount.scene.cameras.main.setBounds(bounds.left * tileSize, bounds.top * tileSize, (bounds.right - bounds.left) * tileSize, (bounds.bottom - bounds.top) * tileSize);
  const ground = new NeighbourGround(
    {
      scene: mount.scene,
      world,
      ownerHeight: map.height,
      ownerBand: mount.band,
      reach: sortReachOf(map.height, places),
      animator: mount.animator,
      loading: mount.loading,
      textures: mount.textures,
      loadChunk: (mapId, cx, cy) => loadClientChunk(world, mapId, cx, cy),
      loadPages: (mapId) => boot.preloadPages(mapId),
      releasePages: (mapId, kept) => boot.atlas.release(world, mapId, kept),
      now: mount.now,
      warn: (line) => console.warn(line),
      pictures: (neighbour, chunks) => new GameMapPictures({ id: neighbour.id, width: neighbour.width, height: neighbour.height, chunkSize: world.manifest.chunkSize, tileSize }, chunks, world.assets, createDomSurface, scenePageAtlas(mount.scene, world.assets), undefined, undefined, mount.active),
    },
    places,
    bounds,
  );
  gameReport.neighbours = ground.readingOf;
  return ground;
}
